import React, { useEffect, useMemo, useState } from "react";
import { get } from "aws-amplify/api";
import { PacmanLoader } from "react-spinners";
import { Row, Col, Button, ListGroup, Alert, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import { PAGE_SIZES } from "utils/pageSizeConfig";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";
import { createColumnHelper } from "@tanstack/react-table";
import "containers/common/List.css";
import "containers/common/Table.css";

function PropertyOwnerList({
  data,
  isLoading,
  isAuthenticated,
  controlType,
  saveRecord,
  deleteRecord,
  updateTableState,
  ownerid,
  propertyidlist,
  idList,
  propertykey,
  propertyid,
  user,
}) {
  const dataSet = "propertyOwners";
  const dataSetPrimaryKey = "property_owner_id";
  const [tableData, setTableData] = useState(data || []);
  const [showAlert, setShowAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [savingRowIds, setSavingRowIds] = useState({});

  const createId = useMemo(() => createElementIdFactory("property-owner-list"), []);
  const tableElementIds = useMemo(() => createReusableTableElementIds(createId), [createId]);
  const columnHelper = useMemo(() => createColumnHelper(), []);

  useEffect(() => {
    setTableData(data || []);
  }, [data]);

  const notifyParent = (nextData, rowIndex, columnId, value) => {
    if (typeof updateTableState !== "function") {
      return;
    }

    if (updateTableState.length >= 4) {
      updateTableState(rowIndex, columnId, value, dataSet);
      return;
    }

    if (updateTableState.length >= 3) {
      updateTableState(rowIndex, columnId, value);
      return;
    }

    updateTableState(nextData, dataSet);
  };

  const removeTempRow = (rowId) => {
    if (!rowId) {
      return;
    }

    setTableData((prev) => {
      const next = prev.filter((item) => item.property_owner_id !== rowId);
      updateTableState?.(next, dataSet);
      return next;
    });
  };

  const refreshOwnerPropertyOwners = async () => {
    if (!ownerid) {
      return;
    }

    try {
      const { body } = await get({
        apiName: "colin",
        path: "/propertyowners",
        options: {
          queryParams: {
            ownerid: String(ownerid),
          },
        },
      }).response;

      const payload = await body.json();
      const refreshedRows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.propertyowners)
          ? payload.propertyowners
          : [];

      setTableData(refreshedRows);
      updateTableState?.(refreshedRows, dataSet);
    } catch (error) {
      console.error("Failed to refresh property owners:", error);
    }
  };

  const updateLocalCell = (rowIndex, columnId, value) => {
    setTableData((prev) => {
      const next = prev.map((row, index) => {
        if (index !== rowIndex) {
          return row;
        }

        return {
          ...row,
          [columnId]: value,
          modified: true,
        };
      });

      notifyParent(next, rowIndex, columnId, value);
      return next;
    });
  };

  const handleSave = async ({ row, value, column }) => {
    const record = row?.original;
    if (!record || typeof saveRecord !== "function") {
      return true;
    }

    let recordToSave = column?.id ? { ...record, [column.id]: value } : record;

    // When use_alternate_text is checked, also set suppress_text = true (matching UAT behavior)
    if (column?.id === "use_alternate_text" && value === true) {
      recordToSave = { ...recordToSave, suppress_text: true };
      updateLocalCell(row.index, "suppress_text", true);
    }

    try {
      const result =
        saveRecord.length >= 3
          ? await saveRecord(recordToSave, dataSet, dataSetPrimaryKey)
          : await saveRecord(dataSet, recordToSave);

      if (record.new_record && result && result.id) {
        setTableData((prev) => {
          const next = prev.map((r, idx) => {
            if (idx !== row.index) {
              return r;
            }

            return {
              ...r,
              [dataSetPrimaryKey]: result.id,
              new_record: false,
            };
          });

          notifyParent(next, row.index, dataSetPrimaryKey, result.id);
          return next;
        });
      }

      return !(result && result.result === false);
    } catch (err) {
      console.error("Save failed:", err);
      return false;
    }
  };

  const handleDelete = async ({ row, requireConfirm = true }) => {
    const record = row?.original;
    if (!record) {
      return;
    }

    if (requireConfirm) {
      const confirmed = window.confirm("Are you sure you want to delete this record?");
      if (!confirmed) {
        return;
      }
    }

    try {
      // Archive the record via saveRecord (matching UAT behavior)
      if (!record.new_record && typeof saveRecord === "function") {
        const archived = { ...record, archived_date: new Date() };
        await (saveRecord.length >= 3
          ? saveRecord(archived, dataSet, dataSetPrimaryKey)
          : saveRecord(dataSet, archived));
      }

      setTableData((prev) => {
        const next = prev.filter((_, index) => index !== row.index);
        updateTableState?.(next, dataSet);
        return next;
      });
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const addRecord = async () => {
    const userName = typeof user === "string" ? user : user?.name || "";

    const newRow = {
      property_id: propertyid || null,
      property_key: "",
      registered_owner: true,
      suppress_address: false,
      suppress_text: false,
      use_alternate_text: false,
      owner_id: ownerid || null,
      sort_order: (tableData?.length || 0) + 1,
      share: 100,
      created_by: userName,
      last_edited_by: userName,
      new_record: true,
      property_owner_id: `temp-${Date.now()}`,
    };

    setTableData((prev) => {
      const next = [...prev, newRow];
      updateTableState?.(next, dataSet);
      return next;
    });
  };

  const ownerDataIds = useMemo(() => (tableData || []).map((row) => row.owner_id).filter(Boolean), [tableData]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("owner_id", {
        id: "owner_id",
        header: "ID",
        cell: (e) => (
          <Link
            id={createId(`owner-link-${e.getValue()}`)}
            to={{
              pathname: `/owners/${e.getValue()}`,
              state: { data: ownerDataIds, propertyidlist: propertyidlist || idList },
            }}
          >
            {e.getValue()}
          </Link>
        ),
        size: 60,
        enableSorting: true,
      }),
      columnHelper.accessor("owner_preamble", {
        id: "owner_preamble",
        header: "Preamble",
        cell: EditableCell,
        enableSorting: false,
      }),
      columnHelper.accessor("owner_name", {
        id: "owner_name",
        header: "Owner Name",
        enableSorting: false,
      }),
      columnHelper.accessor("owner_postamble", {
        id: "owner_postamble",
        header: "Postamble",
        cell: EditableCell,
        size: 85,
        enableSorting: false,
      }),
      columnHelper.accessor("share", {
        id: "share",
        header: "Share",
        cell: EditableCell,
        size: 85,
        meta: { suffix: "%" },
        enableSorting: false,
      }),
      columnHelper.accessor("sort_order", {
        id: "sort_order",
        header: "Sort Order",
        cell: EditableCell,
        size: 85,
        style: { textAlign: "right" },
        enableSorting: false,
      }),
      columnHelper.accessor("suppress_address", {
        id: "suppress_address",
        header: "Suppress Address",
        cell: EditableCell,
        size: 85,
        style: { textAlign: "center" },
        enableSorting: false,
      }),
      columnHelper.accessor("suppress_text", {
        id: "suppress_text",
        header: "Suppress Text",
        cell: EditableCell,
        size: 85,
        style: { textAlign: "center" },
        enableSorting: false,
      }),
      columnHelper.accessor("use_alternate_text", {
        id: "use_alternate_text",
        header: "Use Alternate Text",
        cell: EditableCell,
        size: 85,
        style: { textAlign: "center" },
        enableSorting: false,
      }),
      columnHelper.display({
        id: "delete_property_owner_id",
        header: "",
        cell: ({ row }) => (
          <Button
            id={createId(`remove-from-property-${row.original?.property_owner_id ?? row.id}`)}
            type="button"
            size="sm"
            variant="danger"
            onClick={() => handleDelete({ row })}
          >
            Remove from Property
          </Button>
        ),
        size: 165,
        enableSorting: false,
      }),
    ],
    [columnHelper, createId, idList, ownerDataIds, propertyidlist]
  );

  const columns1 = useMemo(
    () => [
      columnHelper.display({
        id: "back_property_id",
        header: "",
        cell: ({ row }) => {
          const propertyKey = row.original?.property_key;
          if (!propertyKey) {
            return null;
          }

          return (
            <Link
              to={{
                pathname: `/properties/${propertyKey}`,
                state: {
                  tab: "owners",
                  data:
                    Array.isArray(propertyidlist) && propertyidlist.indexOf(propertyKey) >= 0
                      ? propertyidlist
                      : [],
                },
              }}
            >
              <Button type="button" size="sm" variant="primary">
                Back to Property
              </Button>
            </Link>
          );
        },
        size: 130,
        enableSorting: false,
      }),
      columnHelper.accessor("property_owner_id", {
        id: "property_owner_id",
        header: "ID",
        cell: ({ row, getValue }) => (row.original?.new_record ? "" : getValue() || ""),
        size: 60,
        style: { backgroundColor: "#fafafa" },
        enableSorting: true,
      }),
      columnHelper.accessor("property_key", {
        id: "property_key",
        header: "Cityscope Reference",
        cell: (cellProps) => {
          const { row, getValue } = cellProps;
          if (!row.original?.new_record) {
            return <EditableCell {...cellProps} />;
          }

          return (
            <input
              className="form-control form-control-sm"
              type="text"
              value={getValue() || ""}
              autoFocus
              onChange={(event) => {
                updateLocalCell(row.index, "property_key", event.target.value);
                if (showAlert) {
                  setShowAlert(false);
                  setErrorMessage("");
                }
              }}
              onBlur={async (event) => {
                const value = (event.target.value || "").trim();
                const rowId = row.original?.property_owner_id;

                if (!value) {
                  removeTempRow(rowId);
                  setErrorMessage("Enter a Cityscope reference.");
                  setShowAlert(true);
                  return;
                }

                if (savingRowIds[rowId]) {
                  return;
                }

                const recordToSave = {
                  ...row.original,
                  property_key: value,
                  last_edited_by: user?.name,
                };

                try {
                  setSavingRowIds((prev) => ({ ...prev, [rowId]: true }));
                  const result = await saveRecord(recordToSave, dataSet, dataSetPrimaryKey);
                  if (result === false) {
                    removeTempRow(rowId);
                  } else {
                    await refreshOwnerPropertyOwners();
                  }
                } catch (err) {
                  removeTempRow(rowId);
                  const errorMsg = err?.message;
                  if (errorMsg) {
                    setErrorMessage(errorMsg);
                    setShowAlert(true);
                  }
                  console.error("Save failed:", err);
                } finally {
                  setSavingRowIds((prev) => {
                    const next = { ...prev };
                    delete next[rowId];
                    return next;
                  });
                }
              }}
            />
          );
        },
        size: 150,
        enableSorting: true,
      }),
      columnHelper.accessor("street_address", {
        id: "street_address",
        header: "Street Address",
        cell: (cellProps) => cellProps.getValue() || "",
        style: { backgroundColor: "#fafafa" },
        enableSorting: true,
      }),
      columnHelper.accessor("owner_name", {
        id: "owner_name",
        header: "Owner Name",
        cell: ({ getValue }) => getValue() || "",
        style: { backgroundColor: "#fafafa" },
        enableSorting: true,
      }),
      columnHelper.accessor("share", {
        id: "share",
        header: "Share",
        cell: (cellProps) =>
          cellProps.row.original?.new_record ? cellProps.getValue() || "" : <EditableCell {...cellProps} />,
        size: 85,
        style: { textAlign: "right" },
        enableSorting: true,
      }),
      columnHelper.accessor("sort_order", {
        id: "sort_order",
        header: "Sort Order",
        cell: (cellProps) =>
          cellProps.row.original?.new_record ? cellProps.getValue() || "" : <EditableCell {...cellProps} />,
        size: 85,
        style: { textAlign: "right" },
        enableSorting: true,
      }),
      columnHelper.display({
        id: "delete_property_owner_id",
        header: "",
        cell: ({ row }) => (
          <Button
            id={createId(`remove-property-from-owner-${row.original?.property_owner_id ?? row.id}`)}
            type="button"
            size="sm"
            variant="danger"
            onClick={() => handleDelete({ row, requireConfirm: true })}
          >
            Remove Property from Owner
          </Button>
        ),
        size: 250,
        enableSorting: false,
      }),
    ],
    [
      columnHelper,
      createId,
      dataSet,
      dataSetPrimaryKey,
      propertyidlist,
      refreshOwnerPropertyOwners,
      saveRecord,
      savingRowIds,
      showAlert,
      user,
    ]
  );

  const renderList = () => {
    if (!tableData || tableData.length === 0) {
      return null;
    }

    return (
      <ListGroup id={createId("list")}>
        {tableData.map((record, index) => {
          const key = record?.property_owner_id ?? `idx-${index}`;
          const propertyKeyValue = record?.property_key;

          return (
            <Link id={createId(`list-link-${key}`)} key={key} to={`/properties/${propertyKeyValue}`}>
              <ListGroup.Item id={createId(`list-item-${key}`)}>
                {(record?.sort_order || index + 1) + ") " + (propertyKeyValue || "") + " - " + (record?.street_address || "")}
              </ListGroup.Item>
            </Link>
          );
        })}
      </ListGroup>
    );
  };

  const renderList2 = () => {
    if (!tableData || tableData.length === 0) {
      return null;
    }

    return (
      <ListGroup id={createId("list2")}>
        {tableData.map((record, index) => {
          const key = record?.property_owner_id ?? record?.owner_id ?? `idx-${index}`;
          return (
            <Link id={createId(`list2-link-${key}`)} key={key} to={`/owners/${record?.owner_id}`}>
              <ListGroup.Item id={createId(`list2-item-${key}`)} as="a" header={record?.owner_id}>
                {(record?.sort_order || index + 1) + ") " + (record?.owner_name || "")}
              </ListGroup.Item>
            </Link>
          );
        })}
      </ListGroup>
    );
  };

  const activeColumns = controlType === "table1" ? columns1 : columns;

  return (
    <div id={createId("root")} className="OwnerList">
      {isAuthenticated ? (
        <div className="componentRendering">
          <Modal show={showAlert} onHide={() => setShowAlert(false)} centered>
            <Modal.Header closeButton>
              <Modal.Title>Uh Oh!</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Alert variant="danger" className="mb-0">
                {errorMessage}
              </Alert>
            </Modal.Body>
          </Modal>

          {(controlType === "table" || controlType === "table1") && (
            <Row>
              <Col className="text-right mb-2">
                {!ownerid && controlType !== "table" && (
                  <Link id={createId("add-link")} to={`../properties/${propertykey}/owners/new?propertyid=${propertyid}`}>
                    <Button id={createId("add-button")} variant="secondary" size="sm">
                      Add
                    </Button>
                  </Link>
                )}
                {ownerid && (
                  <Button id={createId("add-button")} variant="secondary" size="sm" onClick={addRecord}>
                    Add
                  </Button>
                )}
              </Col>
            </Row>
          )}

          <Row className="show-Container">
            <Col sm={12} md={12} lg={12}>
              {isLoading ? (
                <div className="outer-div" id={createId("loading")}>
                  <div className="sweet-loading inner-div">
                    <PacmanLoader cssOverride={{ display: "block", margin: "0 auto" }} size={20} color={"#e03c31"} loading={isLoading} />
                  </div>
                </div>
              ) : controlType === "table" || controlType === "table1" ? (
                <GenericTable
                  columns={activeColumns}
                  data={tableData || []}
                  section="property-owner-list"
                  elementIds={tableElementIds}
                  pageSize={PAGE_SIZES.propertyOwnershipTab}
                  hasHeaderSorting={true}
                  meta={{
                    updateData: updateLocalCell,
                    handleSave,
                    handleDelete,
                  }}
                />
              ) : controlType === "list2" ? (
                renderList2()
              ) : (
                renderList()
              )}
            </Col>
          </Row>
        </div>
      ) : null}
    </div>
  );
}

export default PropertyOwnerList;
